import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaUser, FaSearch, FaSpinner, FaLock, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const WhatsAppUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(100);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    }
  }, [isAuthenticated]);

  const validateSecurityCode = () => {
    if (securityCode === '8089') {
      setIsAuthenticated(true);
      setShowError(false);
    } else {
      setShowError(true);
      setSecurityCode('');
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.visiontech2030.com/api/admin/users', {
        headers: { Authorization: token }
      });

      const sortedUsers = response.data.sort((a, b) => b.id - a.id);
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredUsers(users);
      return;
    }

    const searchTermLower = term.toLowerCase();
    const filtered = users.filter(user => 
      user.fullName?.toLowerCase().includes(searchTermLower) ||
      user.mobile?.includes(term)
    );
    setFilteredUsers(filtered);
  };

  const formatPhoneNumber = (mobile) => {
    if (!mobile) return '';
    
    // Remove any non-digit characters
    const cleanNumber = mobile.replace(/\D/g, '');
    
    // If number starts with 254, keep it as is
    if (cleanNumber.startsWith('254')) {
      return cleanNumber;
    }
    
    // If number starts with 01, remove 01 and add 254
    if (cleanNumber.startsWith('01')) {
      return `2541${cleanNumber.slice(2)}`;
    }
    
    // If number starts with 07, remove 07 and add 254
    if (cleanNumber.startsWith('07')) {
      return `2547${cleanNumber.slice(2)}`;
    }
    
    return cleanNumber;
  };

  const initiateWhatsAppChat = (mobile) => {
    const formattedNumber = formatPhoneNumber(mobile);
    window.open(`https://wa.me/${formattedNumber}`, '_blank');
  };

  const indexOfLastItem = (currentPage + 1) * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  if (!isAuthenticated) {
    return (
      <div className="whatsapp-chat-user-auth-container">
        <div className="whatsapp-chat-user-auth-card">
          <FaLock className="whatsapp-chat-user-auth-icon" />
          <h2>Security Check</h2>
          <p>Please enter the security code to access the directory</p>
          <div className="whatsapp-chat-user-input-group">
            <input
              type="password"
              maxLength="4"
              value={securityCode}
              onChange={(e) => setSecurityCode(e.target.value)}
              placeholder="Enter code"
              className={`whatsapp-chat-user-auth-input ${showError ? 'error' : ''}`}
            />
            <button 
              className="whatsapp-chat-user-auth-button"
              onClick={validateSecurityCode}
            >
              Verify
            </button>
          </div>
          {showError && (
            <p className="whatsapp-chat-user-auth-error">
              Invalid security code. Please try again.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="whatsapp-chat-user-container">
      <div className="whatsapp-chat-user-header">
        <div className="whatsapp-chat-user-title">
          <FaCheckCircle className="whatsapp-chat-user-verified-icon" />
          <h1>WhatsApp Users Directory</h1>
        </div>
        <div className="whatsapp-chat-user-search">
          <FaSearch className="whatsapp-chat-user-search-icon" />
          <input
            type="text"
            placeholder="Search by name or phone number..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="whatsapp-chat-user-search-input"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="whatsapp-chat-user-loading">
          <FaSpinner className="whatsapp-chat-user-spinner" />
          <p>Loading users...</p>
        </div>
      ) : error ? (
        <div className="whatsapp-chat-user-error">
          <p>{error}</p>
          <button onClick={fetchUsers} className="whatsapp-chat-user-retry">
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="whatsapp-chat-user-grid">
            {currentUsers.map(user => (
              <div key={user.id} className="whatsapp-chat-user-card">
                <div className="whatsapp-chat-user-avatar">
                  <FaUser />
                </div>
                <div className="whatsapp-chat-user-info">
                  <h3>{user.fullName || 'N/A'}</h3>
                  <p className="whatsapp-chat-user-phone">
                    {user.mobile || 'No phone number'}
                  </p>
                  <span className={`whatsapp-chat-user-status ${user.accountStatus?.toLowerCase()}`}>
                    {user.accountStatus || 'Unknown'}
                  </span>
                  <p className="whatsapp-chat-user-date">
                    Joined: {user.createdOn ? format(parseISO(user.createdOn), 'PP') : 'N/A'}
                  </p>
                </div>
                <button 
                  className="whatsapp-chat-user-chat-button"
                  onClick={() => initiateWhatsAppChat(user.mobile)}
                  disabled={!user.mobile}
                >
                  <FaWhatsapp />
                  Chat Now
                </button>
              </div>
            ))}
          </div>

          <div className="whatsapp-chat-user-pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="whatsapp-chat-user-page-button"
            >
              Previous
            </button>
            <span className="whatsapp-chat-user-page-info">
              Page {currentPage + 1}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentUsers.length < itemsPerPage}
              className="whatsapp-chat-user-page-button"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WhatsAppUsers;
